import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shuffle, Search, RefreshCw, PackageOpen } from 'lucide-react';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import ListPagination from '../common/ListPagination.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import SelectField from '../common/SelectField.js';
import CrossingVehicleSelect from './CrossingVehicleSelect.js';
import shipmentCrossingService from '../../services/shipmentCrossingService.js';

const PER_PAGE = 15;
const ML_SHEET_PER_PAGE = 200;

const ShipmentCrossingPage = () => {
  const [searchParams] = useSearchParams();
  const preselectId = searchParams.get('shipment_id');

  const [shipments, setShipments] = useState([]);
  const [mlSheets, setMlSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [mlSheetId, setMlSheetId] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedIds, setSelectedIds] = useState(() => (preselectId ? [preselectId] : []));
  const [vehicle, setVehicle] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const perPage = mlSheetId ? ML_SHEET_PER_PAGE : PER_PAGE;

  const loadMlSheets = useCallback(async () => {
    try {
      const res = await shipmentCrossingService.listCrossableMlSheets();
      setMlSheets(res?.items || []);
    } catch {
      setMlSheets([]);
    }
  }, []);

  const loadShipments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await shipmentCrossingService.listCrossableShipments({
        page,
        perPage,
        search: search || undefined,
        mlSheetId: mlSheetId || undefined,
      });
      setShipments((prev) => mergeLoadedPage(page, res?.items || [], prev));
      setTotalPages(res?.pages || 1);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load shipments');
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, mlSheetId, perPage]);

  useEffect(() => {
    loadMlSheets();
  }, [loadMlSheets]);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  const mlSheetOptions = useMemo(
    () => [
      { value: '', label: 'All ML sheets' },
      ...mlSheets.map((sheet) => ({
        value: sheet.ml_sheet_id,
        label: `${sheet.ml_sheet_id}${sheet.vehicle_number ? ` · ${sheet.vehicle_number}` : ''} (${sheet.shipment_count})`,
      })),
    ],
    [mlSheets],
  );

  const selectedShipments = useMemo(
    () => shipments.filter((s) => selectedIds.includes(s.shipment_id)),
    [shipments, selectedIds],
  );
  const unloadIds = useMemo(
    () => selectedShipments.filter((s) => s.can_unload).map((s) => s.shipment_id),
    [selectedShipments],
  );
  const redispatchIds = useMemo(
    () => selectedShipments.filter((s) => s.can_redispatch).map((s) => s.shipment_id),
    [selectedShipments],
  );

  const toggleSelect = (shipmentId) => {
    setSelectedIds((prev) =>
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId],
    );
  };

  const eligibleOnPage = shipments.filter((s) => s.eligible);
  const allSelectedOnPage =
    eligibleOnPage.length > 0 && eligibleOnPage.every((s) => selectedIds.includes(s.shipment_id));

  const toggleSelectAll = () => {
    const pageIds = eligibleOnPage.map((s) => s.shipment_id);
    if (allSelectedOnPage) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleMlSheetChange = (event) => {
    setMlSheetId(event.target.value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleRefresh = () => {
    loadMlSheets();
    loadShipments();
  };

  const handleUnload = async () => {
    if (unloadIds.length === 0) {
      Alert.error('Select at least one dispatched shipment to unload');
      return;
    }

    try {
      setSubmitting(true);
      const response = await shipmentCrossingService.unloadForCrossing({
        shipmentIds: unloadIds,
        remarks: remarks.trim() || undefined,
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some unloads failed');
      } else {
        Alert.success(response.message || 'Shipments unloaded for crossing');
      }

      setSelectedIds((prev) => prev.filter((id) => !unloadIds.includes(id)));
      setRemarks('');
      loadMlSheets();
      loadShipments();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to unload for crossing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCrossing = async () => {
    if (redispatchIds.length === 0) {
      Alert.error('Unload first, then select unloaded shipments to dispatch');
      return;
    }
    if (!vehicle) {
      Alert.error('Select a vehicle for the crossing');
      return;
    }

    try {
      setSubmitting(true);
      const response = await shipmentCrossingService.createCrossing({
        shipmentIds: redispatchIds,
        newVehicleId: vehicle.id,
        remarks: remarks.trim() || undefined,
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some crossings failed');
      } else {
        Alert.success(response.message || 'Crossing created successfully');
      }

      setSelectedIds([]);
      setVehicle(null);
      setRemarks('');
      loadMlSheets();
      loadShipments();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create crossing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Shipment Crossing"
        subtitle="Hub Admin or Agent at the ML sheet crossing locations: unload first, then dispatch onto another vehicle. The ML sheet, on contract, and commission stay unchanged."
      >
        <Button variant="secondary" icon={RefreshCw} onClick={handleRefresh}>
          Refresh
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by shipment ID, ML sheet, destination, or vehicle..."
                className="login-input w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
            <Button type="submit" variant="primary">Search</Button>
          </form>

          <div className="mb-3">
            <SelectField
              variant="filter"
              label="Filter by ML sheet"
              value={mlSheetId}
              onChange={handleMlSheetChange}
              options={mlSheetOptions}
            />
          </div>

          {loading && page === 1 ? (
            <div className="py-10">
              <Loader fullScreen={false} size="small" message="Loading shipments..." />
            </div>
          ) : shipments.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              {mlSheetId
                ? 'No in-transit shipments on this ML sheet.'
                : 'No in-transit shipments at your crossing location. Only ML sheets that list your hub as a crossing location are shown.'}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-1 pb-2 border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 accent-blue-500"
                  id="select-all-crossing"
                />
                <label htmlFor="select-all-crossing" className="text-xs font-medium text-gray-600">
                  {mlSheetId ? 'Select all eligible on this ML sheet' : 'Select all eligible on this page'}
                </label>
              </div>

              <div className="divide-y divide-gray-100 max-h-[52vh] overflow-y-auto">
                {shipments.map((shipment) => {
                  const selected = selectedIds.includes(shipment.shipment_id);
                  return (
                    <label
                      key={shipment.shipment_id}
                      className={`flex items-start gap-3 p-3 text-sm cursor-pointer ${
                        selected ? 'bg-orange-50/70' : 'hover:bg-gray-50'
                      } ${!shipment.eligible ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!shipment.eligible}
                        onChange={() => toggleSelect(shipment.shipment_id)}
                        className="mt-0.5 w-4 h-4 accent-blue-500"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <strong className="text-gray-800">{shipment.shipment_id}</strong>
                          <span className="capitalize text-gray-600">{shipment.status}</span>
                        </div>
                        <p className="text-gray-600 mt-0.5">
                          {shipment.sender_location} → {shipment.receiver_location}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Current vehicle: {shipment.current_vehicle_number || 'N/A'}
                          {shipment.ml_sheet_id ? ` · ML: ${shipment.ml_sheet_id}` : ''}
                        </p>
                        {shipment.can_unload && (
                          <p className="text-xs text-blue-700 mt-0.5">Ready to unload</p>
                        )}
                        {shipment.can_redispatch && (
                          <p className="text-xs text-green-700 mt-0.5">Unloaded — ready to dispatch</p>
                        )}
                        {!shipment.eligible && shipment.reason && (
                          <p className="text-xs text-amber-700 mt-0.5">{shipment.reason}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {!mlSheetId && (
                <ListPagination
                  currentPage={page}
                  totalPages={totalPages}
                  loading={loading && page > 1}
                  onNext={() => { setLoading(true); setPage((prev) => Math.min(totalPages, prev + 1)); }}
                />
              )}
              {mlSheetId && (
                <p className="text-xs text-gray-500 mt-3">
                  Showing {shipments.length} shipment{shipments.length === 1 ? '' : 's'} on {mlSheetId}
                </p>
              )}
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 h-fit lg:sticky lg:top-4 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Shuffle className="w-4 h-4" />
            </div>
            <h3 className="text-base font-bold text-gray-800">Crossing steps</h3>
          </div>

          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 space-y-1">
            <div>
              <span className="font-semibold text-gray-800">{selectedIds.length}</span> shipment(s) selected
            </div>
            <div className="text-xs text-gray-500">
              Unload: {unloadIds.length} · Dispatch: {redispatchIds.length}
            </div>
            {mlSheetId ? (
              <div className="text-xs text-gray-500">ML sheet: {mlSheetId}</div>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              placeholder="Add a note for unload or crossing..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          <Button
            variant="secondary"
            icon={PackageOpen}
            fullWidth
            loading={submitting}
            disabled={submitting || unloadIds.length === 0}
            onClick={handleUnload}
          >
            1. Unload{unloadIds.length > 0 ? ` (${unloadIds.length})` : ''}
          </Button>

          <CrossingVehicleSelect value={vehicle} onChange={setVehicle} disabled={submitting} />

          <Button
            variant="primary"
            icon={Shuffle}
            fullWidth
            loading={submitting}
            disabled={submitting || redispatchIds.length === 0 || !vehicle}
            onClick={handleCreateCrossing}
          >
            2. Dispatch / Cross{redispatchIds.length > 0 ? ` (${redispatchIds.length})` : ''}
          </Button>

          <p className="text-xs text-gray-400">
            Unload first, then pick a vehicle and dispatch. No labour or remittance changes.
            Hub Admin or Agent at the designated crossing hub can complete both steps.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ShipmentCrossingPage;
